import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useFetch from "../../../../components/useFetch";
import getDataFromApi from "../../../../components/getDataFromApi";
import Sidenav from "../../../navbar/Sidenav";
import {
  BackIcon,
  CloseIcon,
  DatasetIcon,
  DeleteIcon,
  EditIcon,
  FilterIcon,
  HardwareIcon,
  LabelsIcon,
  LinkIcon,
  PlusIcon,
} from "../../../Common/SvgIcons/WorkflowIcons";
import CustomFeatureModal from "./CustomFeatureModal";
import CustomResourceModal from "./CustomResourceModal";
import {
  applyCategoryChange,
  applySpecFieldChange,
  applyUnitChange,
  buildResourceFormDefaults,
  buildResourcePayload,
  coerceResourceFieldValue,
  getCategoryFieldKey,
  getFieldControl,
  getSpecFields,
  getUnitOptions,
  isNestedDiscriminatorField,
  orderSpecEntries,
  getSchemaFieldTitle,
  prettyFieldLabel,
  shouldShowUnit,
} from "./resourceFormSchema";
import "./CreatePlan.scss";
import "./GlobalCatalogManager.scss";
import "../../Admin.scss";
import TitleHeader from "../../../project/header/TitleHeader";
import PathBreadcrumb from "../../../../components/PathBreadcrumb/PathBreadcrumb";
import { Alert, Button } from "reactstrap";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === "object") {
          return String(item.id ?? item._id ?? item.value ?? item.name ?? "");
        }

        return String(item ?? "");
      })
      .filter((item) => item.trim() !== "");
  }

  if (value === null || value === undefined) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[")) {
      try {
        return normalizeIdList(JSON.parse(trimmed));
      } catch { }
    }

    return trimmed.split(",").map((item) => item.trim()).filter(Boolean);
  }

  if (typeof value === "object") {
    const id = value.id ?? value._id ?? value.value ?? value.name ?? "";
    return id === "" ? [] : [String(id)];
  }

  return [String(value)];
};

const extractApiErrorMessage = (error) => {
  const raw =
    error?.body?.message ||
    error?.body?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.detail ||
    error?.message ||
    "";

  return String(raw).replace(/^\d{3}:\s*/, "") || "Request failed. Please try again.";
};

const isAssignedDeleteError = (message) => {
  const lowerMessage = String(message || "").toLowerCase();

  return (
    lowerMessage.includes("is used in") ||
    lowerMessage.includes("assigned to") ||
    lowerMessage.includes("used in plan") ||
    lowerMessage.includes("used by plan")
  );
};

const titleCase = (value) =>
  String(value || "module")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalizeModuleId = (value) =>
  String(value || "").trim().toLowerCase();

const getModuleDisplayName = (name, fallbackValue) =>
  titleCase(String(name || fallbackValue || "core").trim());

const addModuleOption = (list, seen, value, name) => {
  const id = normalizeModuleId(value);
  if (!id || seen.has(id)) return;

  seen.add(id);
  list.push({
    id,
    name: getModuleDisplayName(name, value),
  });
};

export function GlobalResourceManager() {
  return <GlobalCatalogManager mode="resource" />;
}

export function GlobalFeatureManager() {
  return <GlobalCatalogManager mode="feature" />;
}

function GlobalCatalogManager({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isResourceMode = mode === "resource";
  const fromCreatePlan = !!location.state?.fromCreatePlan;

  const { data: planData, refetch: refetchPlan } = useFetch("forms/admin/create_plan", "GET");
  const { data: featureData, refetch: refetchFeatures } = useFetch(
    "admin/feature?filter=is_active=true&page=1&per_page=100",
    "GET"
  );
  const { data: resourceData, refetch: refetchResources } = useFetch(
    "admin/resources?filter=is_active=true&page=1&per_page=100",
    "GET"
  );
  const { data: createFeatureSchema } = useFetch(
    !isResourceMode ? "forms/admin/create_feature" : null,
    "GET"
  );
  const { data: createResourceSchema } = useFetch(
    isResourceMode ? "forms/admin/create_resource" : null,
    "GET"
  );

  const isPlanReady = planData !== undefined;
  const isResourcesReady = resourceData !== undefined;
  const isFeaturesReady = featureData !== undefined;
  const isCatalogBootstrapping = isResourceMode
    ? !isResourcesReady
    : !isPlanReady || !isFeaturesReady;

  const modules = useMemo(() => {
    const enumValues =
      planData?.json_schema?.properties?.["Feature Category"]?.enum || [];

    const enumNames =
      planData?.json_schema?.properties?.["Feature Category"]?.enumNames || [];

    const nextModules = [];
    const seen = new Set();

    enumValues.forEach((value, index) => {
      addModuleOption(nextModules, seen, value, enumNames[index]);
    });

    return nextModules;
  }, [planData]);

  const [selectedModule, setSelectedModule] = useState(
    location.state?.moduleId ? normalizeModuleId(location.state.moduleId) : ""
  );
  const [moduleName, setModuleName] = useState("");
  const [moduleAction, setModuleAction] = useState({
    loadingKey: "",
    error: "",
    success: "",
  });
  const [showModuleCreate, setShowModuleCreate] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(!!location.state?.openCreate);
  const [editFeatureData, setEditFeatureData] = useState(null);
  const [editResourceData, setEditResourceData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogAlert, setCatalogAlert] = useState(null);

  const showCatalogAlert = (message, status = "success") => {
    setCatalogAlert({ message, status });
  };

  useEffect(() => {
    if (!catalogAlert?.message) return undefined;

    const timeoutId = setTimeout(() => {
      setCatalogAlert(null);
    }, 2500);

    return () => clearTimeout(timeoutId);
  }, [catalogAlert]);

  useEffect(() => {
    if (location.state?.moduleId) {
      setSelectedModule(normalizeModuleId(location.state.moduleId));
    }
  }, [location.state?.moduleId]);

  useEffect(() => {
    if (location.state?.openCreate) {
      setShowCreatePanel(true);
    }
  }, [location.state?.openCreate]);

  // Only treat as "needs setup" after plan schema has loaded — empty modules during
  // bootstrap would otherwise close the create drawer and flash wrong empty states.
  const needsModuleSetup =
    !isResourceMode && isPlanReady && modules.length === 0;

  useEffect(() => {
    if (!needsModuleSetup) return;
    setShowCreatePanel(false);
    setShowFeatureModal(false);
    setEditFeatureData(null);
  }, [needsModuleSetup]);

  useEffect(() => {
    if (!modules.length) return;

    const selectedExists = modules.some((module) => module.id === selectedModule);
    if (selectedExists) return;

    setSelectedModule(modules[0].id);
  }, [modules, selectedModule]);

  const features = useMemo(() => {
    return (featureData?.results || []).map((f) => {
      const category = normalizeModuleId(f.category || f.metadata?.category || "core");

      return {
        id: f._id,
        name: f.name,
        description: f.description || f.feature_description || "",
        category,
        price: Number(f.base_price || 0),
        locked: !!f.is_core,
        featureDependencies: normalizeIdList(
          f.metadata?.dependencies ??
          f.metadata?.["Feature Dependencies"] ??
          f.dependencies ??
          f.feature_dependencies
        ),
        resourceDependencies: normalizeIdList(
          f.metadata?.resource_dependencies ??
          f.metadata?.["Resource Dependencies"] ??
          f.resource_dependencies ??
          f.resources_dependencies
        ),
        raw: f,
      };
    });
  }, [featureData]);

  const resources = useMemo(() => {
    return (resourceData?.results || []).map((r) => {
      const metadata = r.metadata || {};
      const category = normalizeModuleId(r.category || metadata.category || "core");

      return {
        id: r._id,
        name: r.name,
        description: r.description || r.resource_description || "",
        category,
        unit: r.unit || "",
        pricePerUnit: Number(r.unit_price || 0),
        min: Number(metadata.min || 0),
        max: Number(metadata.max || 0),
        step: Number(metadata.step || metadata.step_rate || 1),
        scope: String(r.resource_category || "tenant").toLowerCase(),
        type: metadata.ui_type || r.ui_type || "input",
        raw: r,
      };
    });
  }, [resourceData]);

  const filteredFeatures = useMemo(
    () => features.filter((feature) => feature.category === selectedModule),
    [features, selectedModule]
  );

  const searchTerm = catalogSearch.trim().toLowerCase();
  const displayedFeatures = useMemo(() => {
    if (!searchTerm) return filteredFeatures;

    return filteredFeatures.filter((feature) =>
      [feature.name, feature.description, feature.category, feature.id]
        .some((value) => String(value || "").toLowerCase().includes(searchTerm))
    );
  }, [filteredFeatures, searchTerm]);

  const displayedResources = useMemo(() => {
    if (!searchTerm) return resources;

    return resources.filter((resource) =>
      [resource.name, resource.description, resource.category, resource.unit, resource.id]
        .some((value) => String(value || "").toLowerCase().includes(searchTerm))
    );
  }, [resources, searchTerm]);

  const featureNameById = useMemo(() => {
    const map = {};
    features.forEach((feature) => {
      map[feature.id] = feature.name;
    });
    return map;
  }, [features]);

  const resourceNameById = useMemo(() => {
    const map = {};
    resources.forEach((resource) => {
      map[resource.id] = resource.name;
    });
    return map;
  }, [resources]);

  const moduleNameById = useMemo(() => {
    const map = {};
    modules.forEach((module) => {
      map[String(module.id)] = module.name;
      map[String(module.id).toLowerCase()] = module.name;
    });
    return map;
  }, [modules]);

  const resourceById = useMemo(() => {
    const map = {};
    resources.forEach((resource) => {
      map[resource.id] = resource;
    });
    return map;
  }, [resources]);

  const linkedFeaturesByResourceId = useMemo(() => {
    const map = {};
    features.forEach((feature) => {
      feature.resourceDependencies.forEach((resourceId) => {
        const key = String(resourceId);
        if (!map[key]) map[key] = [];
        map[key].push(feature);
      });
    });
    return map;
  }, [features]);

  const refetchAll = () => {
    refetchPlan?.();
    refetchFeatures?.();
    refetchResources?.();
  };

  const handleCreateModule = async () => {
    const name = moduleName.trim();

    if (!name) {
      setModuleAction({
        loadingKey: "",
        error: "Module name is required.",
        success: "",
      });
      showCatalogAlert("Module name is required.", "error");
      return;
    }

    try {
      setModuleAction({ loadingKey: "create", error: "", success: "" });
      await getDataFromApi(`${window.ENV.API_URL}/forms/admin/create_module`, "post", { name });
      const nextModuleId = normalizeModuleId(name);
      setModuleName("");
      setShowModuleCreate(false);
      setShowCreatePanel(false);
      setSelectedModule(nextModuleId);
      setModuleAction({ loadingKey: "", error: "", success: "Module created." });
      showCatalogAlert("Module created.");
      refetchPlan?.();
    } catch (error) {
      const message = extractApiErrorMessage(error) || "Failed to create module.";
      setModuleAction({
        loadingKey: "",
        error: message,
        success: "",
      });
      showCatalogAlert(message, "error");
    }
  };

  const handleDeleteModule = (module) => {
    if (!module?.id || String(module.id).toLowerCase() === "core") return;

    setConfirmDelete({
      type: "module",
      id: module.id,
      name: module.name,
    });
  };

  const deleteModule = async (moduleId) => {
    const module = modules.find((item) => item.id === moduleId);

    try {
      setModuleAction({ loadingKey: `delete:${moduleId}`, error: "", success: "" });
      const response = await getDataFromApi(`${window.ENV.API_URL}/admin/module/${moduleId}`, "delete");
      const apiMessage = extractApiErrorMessage(response);

      if (isAssignedDeleteError(apiMessage)) {
        throw new Error(apiMessage);
      }

      setModuleAction({ loadingKey: "", error: "", success: "Module deleted." });
      if (selectedModule === moduleId) {
        const fallbackModule = modules.find((item) => item.id !== moduleId);
        setSelectedModule(fallbackModule?.id || "");
      }
      refetchPlan?.();
      showCatalogAlert("Module deleted.");
    } catch (error) {
      const message = extractApiErrorMessage(error);
      setModuleAction({ loadingKey: "", error: "", success: "" });
      throw new Error(
        isAssignedDeleteError(message)
          ? `This module is assigned to one or more active plans or contains linked catalog items. Remove those assignments before deleting it.`
          : message || `Failed to delete ${module?.name || "module"}.`
      );
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete?.id || !confirmDelete?.type) return;

    try {
      setDeleteLoading(true);
      setDeleteError(null);

      if (confirmDelete.type === "module") {
        await deleteModule(confirmDelete.id);
        setConfirmDelete(null);
        return;
      }

      const url =
        confirmDelete.type === "feature"
          ? `${window.ENV.API_URL}/admin/feature/${confirmDelete.id}`
          : `${window.ENV.API_URL}/admin/resources/${confirmDelete.id}`;

      const response = await getDataFromApi(url, "delete");
      const apiMessage = extractApiErrorMessage(response);

      if (isAssignedDeleteError(apiMessage)) {
        throw new Error(apiMessage);
      }

      setConfirmDelete(null);
      refetchAll();
    } catch (error) {
      const message = extractApiErrorMessage(error);
      setDeleteError(
        isAssignedDeleteError(message)
          ? `This ${confirmDelete.type} is assigned to one or more active plans or templates. Remove it from those plans or templates before deleting it.`
          : message
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const selectedModuleName =
    modules.find((module) => module.id === selectedModule)?.name || titleCase(selectedModule || "module");
  const totalFeatureDependencyLinks = features.reduce(
    (sum, feature) => sum + feature.featureDependencies.length,
    0
  );
  const totalResourceDependencyLinks = features.reduce(
    (sum, feature) => sum + feature.resourceDependencies.length,
    0
  );
  const assignedResourceCount = Object.keys(linkedFeaturesByResourceId).length;
  const pricedResourceCount = resources.filter((resource) => Number(resource.pricePerUnit || 0) > 0).length;
  const moduleFeatureCounts = useMemo(() => {
    return features.reduce((acc, feature) => {
      acc[feature.category] = (acc[feature.category] || 0) + 1;
      return acc;
    }, {});
  }, [features]);

  return (
    <>

      <div className="mainmenu projects-content de-width">
        {catalogAlert?.message && (
          <div className="global-catalog-page-alert">
            <Alert
              color={catalogAlert.status === "error" ? "danger" : "success"}
              className="mb-0 shadow"
              toggle={() => setCatalogAlert(null)}
            >
              {catalogAlert.message}
            </Alert>
          </div>
        )}

        <PathBreadcrumb
          onBack={() => {
            if (fromCreatePlan) {
              navigate(-1);
              return;
            }

            navigate("/admin");
          }}
          items={
            fromCreatePlan
              ? [
                  { label: "Admin", onClick: () => navigate("/admin") },
                  { label: "Create Plan", onClick: () => navigate(-1) },
                  { label: isResourceMode ? "Resources" : "Features" },
                ]
              : [
                  { label: "Admin", onClick: () => navigate("/admin") },
                  { label: isResourceMode ? "Resources" : "Features" },
                ]
          }
        />


        <div className={`pt-1 w-100 d-a flex-column flex-sm-row justify-content-between gap-3`}>

          <TitleHeader Icon={isResourceMode ? HardwareIcon : LabelsIcon} title1={'Global'} title2={isResourceMode ? "Resources" : "Features"} subtext={isResourceMode
            ? "Define allocation channels, pricing units, and reusable resource boundaries for plan composition."
            : isCatalogBootstrapping
              ? "Loading feature modules and catalog..."
              : needsModuleSetup
              ? "Create a module first. Features are organized under modules, so you need at least one before adding features."
              : "Define cloud execution features, customize pricing structures, and bind endpoints directly to system resources."} />

          {needsModuleSetup || isCatalogBootstrapping ? null : (
            <Button
              color="primary px-3 py-2 btn-click bg-main border-0 btn-full-width"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setShowCreatePanel(true)}
            >
              Create {isResourceMode ? "Resource" : "Feature"}
            </Button>
          )}
        </div>

        {isCatalogBootstrapping ? (
          <div className="container-card global-catalog-empty mt-4">
            <div className="global-inline-loading m-0">
              Loading {isResourceMode ? "resources" : "features"}...
            </div>
          </div>
        ) : needsModuleSetup ? (
          <div className="container-card global-module-setup mt-4">
            <div className="global-module-setup__content">
              <span className="global-module-setup__icon">
                <LabelsIcon className="wf-icon" />
              </span>
              <h5 className="fw-bold mb-2">Create your first module</h5>
              <p className="text-secondary mb-0">
                Modules group features by workspace area (for example Data, Label, or Train).
                Add a module to unlock feature creation.
              </p>

              <div className="global-module-setup__form mt-4">
                <input
                  className="text"
                  value={moduleName}
                  onChange={(event) => {
                    setModuleName(event.target.value);
                    setModuleAction((prev) => ({ ...prev, error: "", success: "" }));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCreateModule();
                    }
                  }}
                  placeholder="Module name (e.g. Data, Label, Train)"
                  autoFocus
                />
                <button
                  type="button"
                  className="global-catalog-primary"
                  onClick={handleCreateModule}
                  disabled={moduleAction.loadingKey === "create"}
                >
                  {moduleAction.loadingKey === "create" ? "Creating..." : "Create Module"}
                </button>
              </div>

              {moduleAction.error ? (
                <div className="global-inline-message text-danger mt-3">{moduleAction.error}</div>
              ) : null}
            </div>
          </div>
        ) : (
          <>
        <div className="admin-metric-row mt-4 pb-2">
          <div
            className="container-card dashboard-cards position-relative overflow-hidden"
            style={{ borderLeft: "4px solid var(--leading-color)" }}
          >
            {isResourceMode ? (
              <HardwareIcon className="wf-icon text-primary border-0" />
            ) : (
              <LabelsIcon className="wf-icon text-primary border-0" />
            )}
            <h6 className="fw-normal mt-3 text-secondary">
              {isResourceMode ? "Total Resources" : "Available Modules"}
            </h6>
            <h5 className="m-0 fw-bold">
              {isResourceMode ? resources.length : modules.length}
            </h5>
            <div className="decor-circle bg-success"></div>
          </div>

          <div className="container-card dashboard-cards position-relative overflow-hidden">
            {isResourceMode ? (
              <DatasetIcon className="wf-icon text-primary border-0" />
            ) : (
              <LabelsIcon className="wf-icon text-primary border-0" />
            )}
            <h6 className="fw-normal mt-3 text-secondary">
              {isResourceMode ? "Assigned Resources" : "Total Features"}
            </h6>
            <h5 className="m-0 fw-bold">
              {isResourceMode ? assignedResourceCount : features.length}
            </h5>
            <div className="decor-circle" style={{ background: "yellow" }}></div>
          </div>

          <div className="container-card dashboard-cards position-relative overflow-hidden">
            <LinkIcon className="wf-icon text-primary border-0" />
            <h6 className="fw-normal mt-3 text-secondary">
              {isResourceMode ? "Resource Links" : "Feature Dependency Links"}
            </h6>
            <h5 className="m-0 fw-bold">
              {isResourceMode ? totalResourceDependencyLinks : totalFeatureDependencyLinks}
            </h5>
            <div className="decor-circle bg-primary"></div>
          </div>

          <div className="container-card dashboard-cards position-relative overflow-hidden">
            <FilterIcon className="wf-icon text-primary border-0" />
            <h6 className="fw-normal mt-3 text-secondary">
              {isResourceMode ? "Priced Resources" : "Resource Dependency Links"}
            </h6>
            <h5 className="m-0 fw-bold text-primary">
              {isResourceMode ? pricedResourceCount : totalResourceDependencyLinks}
            </h5>
            <div className="decor-circle bg-success"></div>
          </div>
        </div>

        <div
          className="global-catalog-layout global-catalog-layout--list-only mt-4"
        >
          <section className="container-card-right global-catalog-list">
            <div className="global-catalog-list-head">
              <div className="global-catalog-title-block">
                <h6 className=" m-0">
                  {isResourceMode ? "Global Resources" : "Global Features"} Explorer
                </h6>
                <p className="text-secondary m-0 mt-1">
                  {isResourceMode
                    ? `${displayedResources.length} resource${displayedResources.length === 1 ? "" : "s"} match your current filters.`
                    : "Select an option from the module categories below to view relative capability listings."}
                </p>
              </div>
              <div className="global-catalog-header-actions">
                {!isResourceMode ? (
                  showModuleCreate ? (
                    <div className="global-module-create global-module-create--header">
                      <input
                        className="text"
                        value={moduleName}
                        onChange={(event) => {
                          setModuleName(event.target.value);
                          setModuleAction((prev) => ({ ...prev, error: "", success: "" }));
                        }}
                        placeholder="New module name..."
                        autoFocus
                      />
                      <button
                        type="button"
                        className="global-catalog-primary"
                        onClick={handleCreateModule}
                        disabled={moduleAction.loadingKey === "create"}
                      >
                        {moduleAction.loadingKey === "create" ? "Creating..." : "Create"}
                      </button>
                      <button
                        type="button"
                        className="global-module-create-close"
                        onClick={() => {
                          setShowModuleCreate(false);
                          setModuleAction((prev) => ({ ...prev, error: "", success: "" }));
                        }}
                        aria-label="Close module creation"
                      >
                        <CloseIcon className="wf-icon text-secondary-small" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="global-catalog-secondary-action global-catalog-module-toggle"
                      onClick={() => {
                        setShowModuleCreate(true);
                        setModuleAction((prev) => ({ ...prev, error: "", success: "" }));
                      }}
                    >
                      <PlusIcon className="wf-icon-small" />
                      Create Module
                    </button>
                  )
                ) : (
                  <div className="d-a flex-row ps-3 global-catalog-search container-card py-2">
                    <FontAwesomeIcon className="plain-button2 text-secondary p-0" icon={faSearch} />
                    <input
                      className="w-100 plain-button2 py-1 px-3"
                      value={catalogSearch}
                      onChange={(event) => setCatalogSearch(event.target.value)}
                      placeholder={`Filter ${isResourceMode ? "resources" : "features"} by name...`}
                    />
                  </div>
                )}
              </div>
            </div>

            {!isResourceMode && (
              <>
                <div className="global-catalog-tabs-row">
                  <div className="global-catalog-tabs">
                    {modules.map((module) => (
                      <button
                        key={module.id}
                        className={`global-catalog-tab ${selectedModule === module.id ? "active" : ""}`}
                        onClick={() => setSelectedModule(module.id)}
                      >
                        <span>{module.name}</span>
                        <b>{moduleFeatureCounts[module.id] || 0}</b>
                        {String(module.id).toLowerCase() !== "core" && (
                          <span
                            className="global-catalog-tab-delete"
                            role="button"
                            tabIndex={0}
                            title={`Delete ${module.name}`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleDeleteModule(module);
                            }}
                            onKeyDown={(event) => {
                              if (event.key !== "Enter" && event.key !== " ") return;
                              event.preventDefault();
                              event.stopPropagation();
                              handleDeleteModule(module);
                            }}
                          >
                            <DeleteIcon className="wf-icon-small text-danger" />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="global-catalog-filter-band">
                  <div className="global-catalog-filter-copy">
                    <h6 className="m-0">
                      <span />
                      {selectedModuleName} Workspace Feature Nodes
                    </h6>
                    <p className="text-secondary m-0 mt-2">
                      Active selection matches {displayedFeatures.length} cloud node{displayedFeatures.length === 1 ? "" : "s"}. Use the search criteria on the right to drill down.
                    </p>
                  </div>
                  <div className="d-a flex-row ps-3 global-catalog-search container-card py-2">
                    <FontAwesomeIcon className="plain-button2 text-secondary p-0" icon={faSearch} />
                    <input
                      className="w-100 plain-button2 py-1 px-3"
                      value={catalogSearch}
                      onChange={(event) => setCatalogSearch(event.target.value)}
                      placeholder="Search Features..."
                    />
                  </div>
                </div>

              </>
            )}

            {isResourceMode ? (
              <div className="global-catalog-grid mt-4">
                {displayedResources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                    linkedFeatures={linkedFeaturesByResourceId[resource.id] || []}
                    onEdit={() => {
                      setEditResourceData(resource);
                      setShowResourceModal(true);
                    }}
                    onDelete={() => setConfirmDelete({
                      type: "resource",
                      id: resource.id,
                      name: resource.name,
                    })}
                  />
                ))}
                {!displayedResources.length && (
                  <EmptyCatalogState
                    label="resources"
                    onCreate={() => setShowCreatePanel(true)}
                  />
                )}
              </div>
            ) : (
              <div className="global-catalog-grid mt-4">
                {displayedFeatures.map((feature) => (
                  <FeatureCard
                    key={feature.id}
                    feature={feature}
                    featureNameById={featureNameById}
                    moduleNameById={moduleNameById}
                    resourceNameById={resourceNameById}
                    resourceById={resourceById}
                    onEdit={() => {
                      setEditFeatureData(feature);
                      setShowFeatureModal(true);
                    }}
                    onDelete={() => setConfirmDelete({
                      type: "feature",
                      id: feature.id,
                      name: feature.name,
                    })}
                  />
                ))}
                {!displayedFeatures.length && (
                  <EmptyCatalogState
                    label="features"
                    moduleName={selectedModuleName}
                    onCreate={() => setShowCreatePanel(true)}
                  />
                )}
              </div>
            )}
          </section>

        </div>
          </>
        )}
      </div>

      {showCreatePanel && !needsModuleSetup && (
        <div className="side-form" onMouseDown={() => setShowCreatePanel(false)}>
          <aside
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="global-catalog-drawer-header">
              <div className="d-a gap-2 w-100">
                <span className="global-catalog-drawer-icon">
                  {isResourceMode ? (
                    <HardwareIcon className="wf-icon-small text-white" />
                  ) : (
                    <LabelsIcon className="wf-icon-small text-white" />
                  )}
                </span>
                <div className="w-100">
                  <div className="d-a justify-content-between w-100 gap-3">
                    <h5 className="m-0 fw-bold">
                      Create {isResourceMode ? "Resource" : "Feature"}
                    </h5>
                    <button
                      type="button"
                      className="global-catalog-drawer-close"
                      onClick={() => setShowCreatePanel(false)}
                      aria-label="Close create panel"
                    >
                      <CloseIcon className="wf-icon text-secondary" />
                    </button>
                  </div>
                  <p className="m-0 text-secondary">
                    {isResourceMode
                      ? "Define a reusable global resource and pricing controls."
                      : `Create a feature in ${selectedModuleName} and connect dependencies.`}
                  </p>
                </div>
              </div>
            </div>

            <div className="global-catalog-drawer-body">
              {isResourceMode ? (
                <InlineResourceCreateForm
                  schema={createResourceSchema?.json_schema}
                  uiOrder={createResourceSchema?.ui_schema?.["ui:order"] || []}
                  showHeading={false}
                  onCreated={() => {
                    refetchAll();
                    setShowCreatePanel(false);
                  }}
                />
              ) : !isPlanReady ? (
                <div className="global-inline-loading">Loading feature form...</div>
              ) : (
                <InlineFeatureCreateForm
                  schema={createFeatureSchema?.json_schema}
                  selectedModule={selectedModule}
                  modules={modules}
                  featureOptions={features}
                  resourceOptions={resources}
                  showHeading={false}
                  onCreated={() => {
                    refetchAll();
                    setShowCreatePanel(false);
                  }}
                />
              )}
            </div>
          </aside>
        </div>
      )}

      <CustomFeatureModal
        showModal={showFeatureModal}
        entitlementId={editFeatureData?.category || selectedModule}
        editData={editFeatureData}
        isEdit={!!editFeatureData}
        featureOptions={features}
        resourceOptions={resources}
        moduleOptions={modules}
        entitlementName={selectedModuleName}
        onClose={() => {
          setShowFeatureModal(false);
          setEditFeatureData(null);
        }}
        onSubmit={() => {
          setShowFeatureModal(false);
          setEditFeatureData(null);
          refetchAll();
        }}
      />

      <CustomResourceModal
        showModal={showResourceModal}
        entitlementId={editResourceData?.category || ""}
        editData={editResourceData}
        isEdit={!!editResourceData}
        onClose={() => {
          setShowResourceModal(false);
          setEditResourceData(null);
        }}
        onSubmit={() => {
          setShowResourceModal(false);
          setEditResourceData(null);
          refetchAll();
        }}
      />

      {confirmDelete && (
        <div className="center-form">
          <div style={{ maxWidth: "520px" }}>
            <div className="cf-head">
              <div>
                <span className="cf-tag text-danger">Delete {confirmDelete.type}</span>
                <h2>{confirmDelete.name}</h2>
              </div>
              <button className="cf-close text-secondary" onClick={() => setConfirmDelete(null)}>
                x
              </button>
            </div>
            <div className="cf-body">
              <p className="fw-semibold text-danger mb-2">
                This action cannot be undone.
              </p>
              {confirmDelete.type === "module" && (
                <p className="text-secondary mb-0">
                  Deleting this module may also affect feature organization and plan setup that depends on it. Please make sure there are no linked features, resources, or plans before continuing.
                </p>
              )}
              {deleteError && (
                <div className="mt-3 p-3 border border-danger text-danger" role="alert">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="cf-footer d-a justify-content-between w-100">
              <button className="container-card px-4 py-2 text" onClick={() => setConfirmDelete(null)}>
                CANCEL
              </button>
              <button className="cf-submit bg-danger px-4" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mobile-view-bottom"></div>
    </>
  );
}

function InlineResourceCreateForm({ schema, uiOrder = [], onCreated, showHeading = true }) {
  const [formData, setFormData] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!schema?.properties) return;

    setFormData(buildResourceFormDefaults(schema));
    setSubmitError(null);
    setSubmitSuccess(null);
    setErrors({});
  }, [schema]);

  const categoryKey = getCategoryFieldKey(schema);
  const categoryOptions = schema?.properties?.[categoryKey] || {};
  const selectedCategory = formData[categoryKey];
  const unitOptions = getUnitOptions(schema, formData);
  const showUnit = shouldShowUnit(schema, formData);
  const specFields = getSpecFields(schema, formData);

  const fieldCatalog = useMemo(() => {
    const fields = {};

    if (schema?.properties?.["Resource Name"]) {
      fields["Resource Name"] = schema.properties["Resource Name"];
    }
    if (schema?.properties?.["Resource Description"]) {
      fields["Resource Description"] = schema.properties["Resource Description"];
    }
    if (categoryKey && categoryOptions) {
      fields[categoryKey] = categoryOptions;
    }

    Object.entries(specFields || {}).forEach(([key, fieldSchema]) => {
      fields[key] = fieldSchema;
    });

    if (showUnit) {
      fields.Unit =
        schema?.properties?.Unit || {
          type: "string",
          title: "Unit",
          enum: unitOptions.enum || [],
          enumNames: unitOptions.enumNames || unitOptions.enum || [],
        };
    }

    fields["Unit Price"] =
      schema?.properties?.["Unit Price"] ||
      schema?.properties?.unit_price || {
        type: "number",
        title: "Unit Price",
        default: 0,
      };

    fields.min_value =
      schema?.properties?.min_value || {
        type: "number",
        title: "Min Value",
        default: 0,
      };

    fields.max_value =
      schema?.properties?.max_value || {
        type: "number",
        title: "Max Value",
        default: 100,
      };

    return fields;
  }, [schema, categoryKey, categoryOptions, specFields, showUnit, unitOptions]);

  const orderedFieldKeys = useMemo(() => {
    const available = new Set(Object.keys(fieldCatalog));
    const aliasGroups = [
      ["Unit Price", "unit_price"],
      ["Category", "Resource Category"],
    ];

    const resolveKey = (key) => {
      if (available.has(key)) return key;
      for (const group of aliasGroups) {
        if (!group.includes(key)) continue;
        const hit = group.find((candidate) => available.has(candidate));
        if (hit) return hit;
      }
      return null;
    };

    const ordered = [];
    const seen = new Set();

    (Array.isArray(uiOrder) ? uiOrder : []).forEach((key) => {
      if (!key || key === "*") return;
      const resolved = resolveKey(key);
      if (!resolved || seen.has(resolved)) return;
      if (resolved === "Unit" && !showUnit) return;
      ordered.push(resolved);
      seen.add(resolved);
    });

    Object.keys(fieldCatalog).forEach((key) => {
      if (seen.has(key)) return;
      if (key === "Unit" && !showUnit) return;
      ordered.push(key);
      seen.add(key);
    });

    return ordered;
  }, [fieldCatalog, uiOrder, showUnit]);

  const update = (key, value) => {
    const nextValue = coerceResourceFieldValue(key, value);

    setFormData((prev) => {
      if (key === categoryKey) return applyCategoryChange(prev, schema, nextValue);
      if (key === "Unit") return applyUnitChange(prev, schema, nextValue);
      if (isNestedDiscriminatorField(schema, prev, key)) {
        return applySpecFieldChange(prev, schema, key, nextValue);
      }
      return { ...prev, [key]: nextValue };
    });
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const resetForm = () => {
    if (!schema?.properties) return;
    setFormData(buildResourceFormDefaults(schema));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(formData["Resource Name"] || "").trim()) {
      nextErrors["Resource Name"] = "Resource name is required.";
    }
    if (!String(formData[categoryKey] || "").trim()) {
      nextErrors[categoryKey] = "Category is required.";
    }
    if (showUnit && !String(formData.Unit || "").trim()) {
      nextErrors.Unit = "Unit is required.";
    }

    const unitPrice =
      formData["Unit Price"] === "" ? 0 : Number(formData["Unit Price"] || 0);
    const min = formData.min_value === "" ? 0 : Number(formData.min_value || 0);
    const max = formData.max_value === "" ? 0 : Number(formData.max_value || 0);

    if (Number.isNaN(unitPrice) || unitPrice < 0) {
      nextErrors["Unit Price"] = "Base cost must be a valid positive number.";
    }
    if (Number.isNaN(min) || min < 0) {
      nextErrors.min_value = "Minimum value cannot be negative.";
    }
    if (Number.isNaN(max) || max < 0) {
      nextErrors.max_value = "Maximum value cannot be negative.";
    }
    if (min > 0 && max > 0 && max < min) {
      nextErrors.max_value = "Maximum value cannot be less than minimum value.";
    }

    setErrors(nextErrors);
    const firstError = Object.values(nextErrors).find(Boolean);
    setSubmitError(firstError || null);
    return !firstError;
  };

  const handleSubmit = () => {
    if (!schema?.properties || !validate()) return;

    const payload = buildResourcePayload(formData, schema);

    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    getDataFromApi(`${window.ENV.API_URL}/forms/admin/create_resource`, "post", payload)
      .then(() => {
        setSubmitSuccess("Resource created successfully.");
        resetForm();
        onCreated?.();
      })
      .catch((error) => {
        setSubmitError(extractApiErrorMessage(error));
      })
      .finally(() => setSubmitLoading(false));
  };

  const renderFieldControl = (key, fieldSchema) => {
    if (key === "Resource Name") {
      return (
        <input
          className="text"
          value={formData["Resource Name"] || ""}
          onChange={(event) => update("Resource Name", event.target.value)}
          placeholder="Give this resource a name"
        />
      );
    }

    if (key === "Resource Description") {
      return (
        <textarea
          className="text"
          value={formData["Resource Description"] || ""}
          onChange={(event) => update("Resource Description", event.target.value)}
          placeholder="Describe what this resource controls"
        />
      );
    }

    if (key === categoryKey) {
      return (
        <select
          className="text"
          value={selectedCategory || ""}
          onChange={(event) => update(categoryKey, event.target.value)}
        >
          {(categoryOptions.enum || []).map((value, index) => (
            <option key={value} value={value} className="text">
              {categoryOptions.enumNames?.[index] || value}
            </option>
          ))}
        </select>
      );
    }

    if (key === "Unit") {
      return (
        <select
          className="text"
          value={formData.Unit || ""}
          onChange={(event) => update("Unit", event.target.value)}
        >
          {(unitOptions.enum || []).map((value, index) => (
            <option key={value} value={value} className="text">
              {unitOptions.enumNames?.[index] || value}
            </option>
          ))}
        </select>
      );
    }

    if (key === "Unit Price" || key === "min_value" || key === "max_value") {
      return (
        <input
          type="number"
          min={0}
          className="text"
          value={formData[key] ?? (key === "Unit Price" ? "" : 0)}
          onChange={(event) => update(key, event.target.value)}
        />
      );
    }

    const control = getFieldControl(fieldSchema);
    if (control.kind === "select") {
      return (
        <select
          className="text"
          value={formData[key] || ""}
          onChange={(event) => update(key, event.target.value)}
        >
          {control.enum.map((value, index) => (
            <option key={value} value={value} className="text">
              {control.enumNames[index] || value}
            </option>
          ))}
        </select>
      );
    }

    if (control.kind === "boolean") {
      return (
        <input
          type="checkbox"
          checked={!!formData[key]}
          onChange={(event) => update(key, event.target.checked)}
        />
      );
    }

    return (
      <input
        type={control.kind === "number" ? "number" : "text"}
        min={control.kind === "number" ? 0 : undefined}
        className="text"
        value={formData[key] ?? ""}
        onChange={(event) => update(key, event.target.value)}
      />
    );
  };

  if (!schema?.properties) {
    return <div className="global-inline-loading">Loading resource form...</div>;
  }

  const fullWidthKeys = new Set([
    "Resource Name",
    "Resource Description",
    categoryKey,
  ]);

  return (
    <div className="global-inline-form global-resource-create-form">
      {showHeading && (
        <div className="global-feature-create-head">
          <h6 className="d-block fw-bold mt-2">Create Global Resource</h6>
          <p>Define a reusable resource, pricing unit, and allocation bounds.</p>
        </div>
      )}

      <div className="global-drawer-form-stack">
        <div className="global-inline-grid global-resource-fields-grid">
          {orderedFieldKeys.map((key) => {
            const fieldSchema = fieldCatalog[key];
            if (!fieldSchema) return null;

            const label =
              key === categoryKey
                ? prettyFieldLabel(categoryKey, categoryOptions)
                : getSchemaFieldTitle(schema, key, prettyFieldLabel(key, fieldSchema));

            const required =
              key === "Resource Name" ||
              key === categoryKey ||
              key === "Unit" ||
              key === "Unit Price";

            return (
              <InlineField
                key={key}
                className={fullWidthKeys.has(key) ? "global-inline-field--full" : ""}
                label={label}
                error={errors[key]}
                required={required}
              >
                {renderFieldControl(key, fieldSchema)}
              </InlineField>
            );
          })}
        </div>
      </div>

      <div className="global-resource-create-footer">
        <InlineSubmit
          label="Create Resource"
          loadingLabel="Creating..."
          loading={submitLoading}
          error={submitError}
          success={submitSuccess}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
}


function InlineFeatureCreateForm({
  schema,
  selectedModule,
  modules,
  featureOptions = [],
  resourceOptions = [],
  onCreated,
  showHeading = true,
}) {
  const [formData, setFormData] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!schema?.properties) return;

    const defaults = {};
    const hasResourceDependencyField =
      Boolean(schema?.properties?.["Resource Dependencies"]) ||
      resourceOptions.length > 0;

    Object.entries(schema.properties).forEach(([key, val]) => {
      if (key === "Feature Category") {
        const matched = (val.enum || []).find(
          (item) => String(item).toLowerCase() === String(selectedModule).toLowerCase()
        );
        defaults[key] = matched || selectedModule;
      } else if (key === "Feature Dependencies" || key === "Resource Dependencies") {
        defaults[key] = [];
      } else if (val.default !== undefined) {
        defaults[key] = val.default;
      } else if (val.type === "boolean") {
        defaults[key] = false;
      } else if (key === "Price") {
        defaults[key] = 0;
      } else {
        defaults[key] = "";
      }
    });

    if (hasResourceDependencyField && !Object.prototype.hasOwnProperty.call(defaults, "Resource Dependencies")) {
      defaults["Resource Dependencies"] = [];
    }

    setFormData(defaults);
    setSubmitError(null);
    setSubmitSuccess(null);
    setErrors({});
  }, [schema, selectedModule, resourceOptions.length]);

  const resolveCategory = () => {
    if (formData["Feature Category"]) {
      return formData["Feature Category"];
    }

    const enums = schema?.properties?.["Feature Category"]?.enum || [];
    const matched = enums.find(
      (item) => String(item).toLowerCase() === String(selectedModule).toLowerCase()
    );
    return matched || selectedModule;
  };

  const update = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const resetForm = () => {
    if (!schema?.properties) return;

    const defaults = {};
    const hasResourceDependencyField =
      Boolean(schema?.properties?.["Resource Dependencies"]) ||
      resourceOptions.length > 0;

    Object.entries(schema.properties).forEach(([key, val]) => {
      if (key === "Feature Category") defaults[key] = resolveCategory();
      else if (key === "Feature Dependencies" || key === "Resource Dependencies") defaults[key] = [];
      else if (val.default !== undefined) defaults[key] = val.default;
      else if (val.type === "boolean") defaults[key] = false;
      else if (key === "Price") defaults[key] = 0;
      else defaults[key] = "";
    });
    if (hasResourceDependencyField && !Object.prototype.hasOwnProperty.call(defaults, "Resource Dependencies")) {
      defaults["Resource Dependencies"] = [];
    }
    setFormData(defaults);
  };

  const validate = () => {
    const nextErrors = {};
    const name = String(formData["Feature Name"] || "").trim();
    const description = String(formData["Feature Description"] || "").trim();
    const rawPrice = formData.Price;
    const price = Number(rawPrice);
    const category = resolveCategory();

    if (!name) nextErrors["Feature Name"] = "Feature name is required.";
    else if (name.length < 3) nextErrors["Feature Name"] = "Feature name must be at least 3 characters.";
    else if (name.length > 100) nextErrors["Feature Name"] = "Feature name must be under 100 characters.";

    if (description.length > 500) {
      nextErrors["Feature Description"] = "Feature description must be under 500 characters.";
    }

    if (!category) nextErrors["Feature Category"] = "Feature category is required.";

    if (rawPrice === "" || rawPrice === null || rawPrice === undefined) {
      nextErrors.Price = "Price is required.";
    } else if (Number.isNaN(price)) {
      nextErrors.Price = "Price must be a valid number.";
    } else if (price < 0) {
      nextErrors.Price = "Price cannot be negative.";
    } else if (price > 1000000) {
      nextErrors.Price = "Price is too large.";
    }

    setErrors(nextErrors);
    const firstError = Object.values(nextErrors).find(Boolean);
    setSubmitError(firstError || null);
    return !firstError;
  };

  const handleSubmit = () => {
    if (!schema?.properties || !validate()) return;

    const featureDependencies = normalizeIdList(formData["Feature Dependencies"]);
    const resourceDependencies = normalizeIdList(formData["Resource Dependencies"]);
    const { ["Feature Category"]: _, ...rest } = formData;

    const payload = {
      ...rest,
      Avalability: true,
      "Feature Name": String(formData["Feature Name"] || "").trim(),
      "Feature Description": String(formData["Feature Description"] || "").trim(),
      "Feature Dependencies": featureDependencies,
      Price: Number(formData.Price || 0),
      Category: resolveCategory(),
    };

    if (
      schema?.properties?.["Resource Dependencies"] ||
      resourceOptions.length > 0 ||
      Object.prototype.hasOwnProperty.call(formData, "Resource Dependencies")
    ) {
      payload["Resource Dependencies"] = resourceDependencies;
    }

    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    getDataFromApi(`${window.ENV.API_URL}/forms/admin/create_feature`, "post", payload)
      .then(() => {
        setSubmitSuccess("Feature created successfully.");
        resetForm();
        onCreated?.();
      })
      .catch((error) => {
        setSubmitError(extractApiErrorMessage(error));
      })
      .finally(() => setSubmitLoading(false));
  };

  if (!schema?.properties) {
    return <div className="global-inline-loading">Loading feature form...</div>;
  }

  const hasResourceDependencyField =
    Boolean(schema.properties["Resource Dependencies"]) ||
    resourceOptions.length > 0;
  const categoryOptions = (() => {
    const enumValues = schema.properties["Feature Category"]?.enum || [];
    const enumNames = schema.properties["Feature Category"]?.enumNames || [];

    if (enumValues.length) {
      return enumValues.map((value, index) => ({
        id: value,
        name: enumNames[index] || titleCase(value),
      }));
    }

    return modules.map((module) => ({
      id: module.id,
      name: module.name,
    }));
  })();
  const selectedModuleName =
    categoryOptions.find((module) => String(module.id).toLowerCase() === String(resolveCategory()).toLowerCase())?.name ||
    modules.find((module) => module.id === selectedModule)?.name ||
    titleCase(selectedModule);

  return (
    <div className="global-inline-form global-feature-create-form">
      {showHeading && (
        <div className="global-feature-create-head">
          <h6 className="d-block fw-bold mt-2">Create {selectedModuleName} Feature</h6>
          <p>Attach a feature to this module and connect any dependencies.</p>
        </div>
      )}

      <div className="global-drawer-form-stack">
        <section className="global-drawer-form-section">
          <h6>Feature Details</h6>

          <InlineField label={getSchemaFieldTitle(schema, "Feature Name", "Feature Name")} error={errors["Feature Name"]} required>
            <input
              className="text"
              value={formData["Feature Name"] || ""}
              onChange={(event) => update("Feature Name", event.target.value)}
              placeholder="Give this feature a name"
            />
          </InlineField>

          <InlineField label={getSchemaFieldTitle(schema, "Feature Category", "Feature Module")} error={errors["Feature Category"]} required>
            <select
              className="text"
              value={formData["Feature Category"] || resolveCategory()}
              onChange={(event) => update("Feature Category", event.target.value)}
            >
              {categoryOptions.map((module) => (
                <option key={module.id} value={module.id} className="text">
                  {module.name}
                </option>
              ))}
            </select>
          </InlineField>

          <InlineField label={getSchemaFieldTitle(schema, "Feature Description", "Feature Description")} error={errors["Feature Description"]}>
            <textarea
              className="text"
              value={formData["Feature Description"] || ""}
              onChange={(event) => update("Feature Description", event.target.value)}
              placeholder="Describe the capability or business impact"
            />
          </InlineField>
        </section>

        <section className="global-drawer-form-section">
          <h6>Dependencies</h6>

          <DependencyPicker
            label={getSchemaFieldTitle(schema, "Feature Dependencies", "Feature Dependencies")}
            values={formData["Feature Dependencies"]}
            options={schema.properties["Feature Dependencies"]?.items}
            fallbackOptions={featureOptions}
            modalStyle
            onChange={(value) => update("Feature Dependencies", value)}
          />

          {hasResourceDependencyField && (
            <DependencyPicker
              label={getSchemaFieldTitle(schema, "Resource Dependencies", "Resource Dependencies")}
              values={formData["Resource Dependencies"]}
              options={schema.properties["Resource Dependencies"]?.items}
              fallbackOptions={resourceOptions}
              modalStyle
              onChange={(value) => update("Resource Dependencies", value)}
            />
          )}
        </section>

        <section className="global-drawer-form-section">
          <h6>Pricing</h6>

          <div className="global-inline-grid global-inline-grid--pricing">
            <InlineField label={getSchemaFieldTitle(schema, "Price", "Price")} error={errors.Price} required>
              <input
                type="number"
                min={0}
                step="0.01"
                className="text"
                value={formData.Price ?? ""}
                onChange={(event) => update("Price", event.target.value)}
              />
            </InlineField>

            <label className="global-inline-switch global-feature-switch global-drawer-toggle">
              <span>Core Feature</span>
              <input
                type="checkbox"
                checked={!!formData["Core Feature"]}
                onChange={(event) => update("Core Feature", event.target.checked)}
              />
              <span className="global-inline-switch-track"></span>
              <div className="cf-note text-secondary">
                {formData["Core Feature"]
                  ? "Bundled feature. Turn off Core to keep it a la carte."
                  : "A la carte add-on. Turn on Core to bundle."}
              </div>
            </label>
          </div>
        </section>
      </div>

      <div className="global-feature-create-footer">
        {submitError && <div className="global-inline-message text-danger wf-icon-red plain-wh">{submitError}</div>}
        {submitSuccess && <div className="global-inline-message text-primary">{submitSuccess}</div>}

        <button className="cf-submit bg-primary" onClick={handleSubmit} disabled={submitLoading}>
          {submitLoading ? "Creating..." : "Create Feature"}
        </button>
      </div>
    </div>
  );
}

function InlineField({ label, error, required, children, className = "" }) {
  return (
    <label className={`global-inline-field ${className} ${error ? "error" : ""}`.trim()}>
      <span>
        {label}
        {required && <b className="global-inline-required"> *</b>}
      </span>
      {children}
      {error && <small className="text-danger">{error}</small>}
    </label>
  );
}

function DependencyPicker({ label, values, options, fallbackOptions = [], modalStyle = false, onChange }) {
  const selectedValues = Array.isArray(values) ? values : [];
  const schemaValues = options?.enum || [];
  const schemaNames = options?.enumNames || [];
  const enumValues = [...schemaValues];
  const enumNames = [...schemaNames];
  const seen = new Set(enumValues.map((value) => String(value)));

  fallbackOptions.forEach((option) => {
    const value =
      option?.id ??
      option?._id ??
      option?.value ??
      option?.raw?._id ??
      "";

    if (value === "" || value === null || value === undefined) return;

    const key = String(value);
    if (seen.has(key)) {
      const existingIndex = enumValues.findIndex((item) => String(item) === key);
      const fallbackName = option?.name || option?.label || option?.raw?.name || "";
      if (
        existingIndex >= 0 &&
        fallbackName &&
        (!enumNames[existingIndex] || String(enumNames[existingIndex]) === key)
      ) {
        enumNames[existingIndex] = fallbackName;
      }
      return;
    }

    enumValues.push(key);
    enumNames.push(option?.name || option?.label || option?.raw?.name || key);
    seen.add(key);
  });

  const availableValues = enumValues.filter(
    (value) => !selectedValues.some((selected) => String(selected) === String(value))
  );
  const removeAt = (removeIndex) => {
    onChange(selectedValues.filter((_, index) => index !== removeIndex));
  };

  const getLabel = (value) => {
    const index = enumValues.findIndex((item) => String(item) === String(value));
    return index >= 0 ? enumNames[index] || value : value;
  };
  const placeholderLabel = label.toLowerCase().includes("resource")
    ? "Select Resource Dependencies..."
    : label.toLowerCase().includes("feature")
      ? "Select Feature dependencies..."
      : "Select dependency...";

  return (
    <label className={`global-inline-field ${modalStyle ? "global-inline-field--modal" : ""}`}>
      <span>{label}</span>
      <div className={`global-inline-chips ${modalStyle ? "global-inline-chips--modal" : ""} ${!selectedValues.length ? "is-empty" : ""}`}>
        {!enumValues.length ? (
          <em>No dependencies available</em>
        ) : selectedValues.length ? selectedValues.map((value, selectedIndex) => (
          <span key={`${String(value)}-${selectedIndex}`} className="global-inline-chip">
            {getLabel(value)}
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                removeAt(selectedIndex);
              }}
            >
              x
            </button>
          </span>
        )) : (
          <em>No dependencies selected</em>
        )}
      </div>
      <select
        className="text"
        value=""
        disabled={!availableValues.length}
        onChange={(event) => {
          const selected = event.target.value;
          if (!selected) return;
          if (selectedValues.some((value) => String(value) === String(selected))) return;
          onChange([...selectedValues, selected]);
          event.target.value = "";
        }}
      >
        <option value="" disabled>
          {availableValues.length ? placeholderLabel : "No dependencies available"}
        </option>
        {availableValues.map((value) => (
          <option key={String(value)} value={value} className="text">
            {getLabel(value)}
          </option>
        ))}
      </select>
    </label>
  );
}

function InlineSubmit({ label, loadingLabel, loading, error, success, onClick }) {
  return (
    <>
      {error && <div className="global-inline-message text-danger  wf-icon-red plain-wh">{error}</div>}
      {success && <div className="global-inline-message text-primary">{success}</div>}
      <button className="global-catalog-primary" onClick={onClick} disabled={loading}>
        {/* <PlusIcon className="wf-icon-small text-white" /> */}
        {loading ? loadingLabel : label}
      </button>
    </>
  );
}

function ResourceCard({ resource, linkedFeatures, onEdit, onDelete }) {
  return (
    <div className="container-card global-catalog-card global-catalog-card--resource">
      <div className="global-catalog-card-head">
        <div className="global-catalog-card-title">
          <span className="global-catalog-card-icon global-catalog-card-icon--resource">
            <HardwareIcon className="wf-icon-small" />
          </span>
          <div>
            <h5 className="fw-bold m-0">{resource.name}</h5>
            <small>{resource.scope === "user" ? "User scoped resource" : "Tenant scoped resource"}</small>
          </div>
        </div>
        <strong className="global-catalog-price">
          ${resource.pricePerUnit}
          <small>PER {resource.unit || "UNIT"}</small>
        </strong>
      </div>

      <p className="text-secondary mt-3 mb-0 global-catalog-description">
        {resource.description || "No description added."}
      </p>

      <div className="global-catalog-linked mt-3">
        <div className="global-catalog-linked-head">
          <strong>ASSIGNED FEATURES</strong>
          <span>{linkedFeatures.length} assigned</span>
        </div>
        {linkedFeatures.length ? linkedFeatures.slice(0, 4).map((feature) => (
          <div key={feature.id} className="global-catalog-linked-row">
            <span>{feature.name}</span>
            <b>${Number(feature.price || 0).toFixed(2)}</b>
          </div>
        )) : (
          <div className="global-catalog-linked-row text-secondary">No linked features</div>
        )}
      </div>

      <div className="global-catalog-target-node">
        <strong>TARGET ALLOCATION RANGE</strong>
        <div>
          <DatasetIcon className="wf-icon-small" />
          <span>{resource.min} to {resource.max || "unlimited"} {resource.unit || "units"}</span>
          <b>${Number(resource.pricePerUnit || 0).toFixed(2)}</b>
        </div>
      </div>

      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

function FeatureCard({ feature, featureNameById, moduleNameById, resourceNameById, resourceById, onEdit, onDelete }) {
  const dependencyNames = feature.featureDependencies.map((id) => featureNameById[id] || String(id));
  const resourceItems = feature.resourceDependencies.map((id) => ({
    id,
    name: resourceNameById[id] || String(id),
    pricePerUnit: resourceById[id]?.pricePerUnit || 0,
    unit: resourceById[id]?.unit || "unit",
  }));
  const moduleName =
    moduleNameById[String(feature.category)] ||
    moduleNameById[String(feature.category).toLowerCase()] ||
    titleCase(feature.category);

  return (
    <div className="container-card global-catalog-card global-catalog-card--feature">
      <div className="global-catalog-card-head">
        <div className="global-catalog-card-title">
          <span className="global-catalog-card-icon">
            <LabelsIcon className="wf-icon-small" />
          </span>
          <div>
            <h5 className="fw-bold m-0">{feature.name}</h5>
            <small>{moduleName} feature</small>
          </div>
        </div>
        <strong className="global-catalog-price">
          ${feature.price}
          <small>PER REQUEST</small>
        </strong>
      </div>

      <p className="text-secondary mt-3 mb-0 global-catalog-description">
        {feature.description || "No description added."}
      </p>

      <div className="global-catalog-linked mt-3">
        <div className="global-catalog-linked-head">
          <strong>FEATURE DEPENDENCIES</strong>
          <span>{dependencyNames.length} dependencies</span>
        </div>
        {dependencyNames.length ? dependencyNames.slice(0, 3).map((name) => (
          <div key={name} className="global-catalog-linked-row">
            <span>{name}</span>
          </div>
        )) : (
          <div className="global-catalog-linked-row text-secondary">No feature dependencies</div>
        )}
      </div>

      <div className="global-catalog-target-node">
        <div className="global-catalog-linked-head">
          <strong>ASSIGNED RESOURCES</strong>
          <span>{resourceItems.length} dependencies</span>
        </div>
        {resourceItems.length ? resourceItems.slice(0, 3).map((resource) => (
          <div key={resource.id}>
            <HardwareIcon className="wf-icon-small" />
            <span>{resource.name}</span>
            <b>${Number(resource.pricePerUnit || 0).toFixed(2)} / {resource.unit}</b>
          </div>
        )) : (
          <small>No resource dependency configured</small>
        )}
      </div>

      <CardActions onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

function CardActions({ onEdit, onDelete }) {
  return (
    <div className="global-catalog-actions">
      <button className="plain-button2 text-secondary fw-bold" onClick={onEdit}>
        <EditIcon className="wf-icon-small me-1" />
        Configure
      </button>
      <button className="plain-button2 text-secondary fw-bold" onClick={onDelete}>
        <DeleteIcon className="wf-icon-small me-1" />
        Remove
      </button>
    </div>
  );
}

function EmptyCatalogState({ label, moduleName, onCreate }) {
  const singularLabel = label === "resources" ? "Resource" : "Feature";
  const EmptyIcon = label === "resources" ? HardwareIcon : LabelsIcon;

  return (
    <div className="container-card global-catalog-empty">
      <EmptyIcon className="wf-icon text-primary" />
      <h6 className="fw-bold mt-3">No {label}{moduleName ? ` in ${moduleName}` : ""}</h6>
      <p className="text-secondary mb-0">Create one here to populate this catalog.</p>
      {onCreate && (
        <button type="button" className="global-catalog-primary mt-3" onClick={onCreate}>
          {/* <PlusIcon className="wf-icon-small text-white" /> */}
          Create {singularLabel}
        </button>
      )}
    </div>
  );
}
